/**
 * useHandoffs Hook
 * Hook to fetch handoffs data for dashboard stats
 */

import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { useTeamMember } from '../../useTeamMember';
import {
  getHandoffHistory,
  HandoffHistoryItem,
  HandoffHistoryResponse,
} from '../../../api/handoff';
import { EMPTY_STATUS_COUNTS, HandoffStatusCounts } from '../../../utils/handoffStatus';
import { HandoffStatus } from '../../../types/handoff.types';

const PAGE_SIZE = 20;

interface UseHandoffsParams {
  dateRange: {
    from: Date | null;
    to: Date | null;
  };
  status?: HandoffStatus | 'all';
  enabled?: boolean;
}

interface UseHandoffsReturn {
  handoffs: HandoffHistoryItem[];
  count: number;
  stats: HandoffStatusCounts & { total: number };
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch handoffs history list with optional filtering and pagination
 * Uses the /handoff-history endpoint with infinite scroll support
 */
export const useHandoffs = ({
  // dateRange is accepted for call-site compatibility but intentionally unused:
  // the handoff queue is all-time, not date-windowed.
  status,
  enabled = true,
}: UseHandoffsParams): UseHandoffsReturn => {
  const { organization } = useOrganizationStore();
  const { teamMemberId } = useTeamMember();
  const organizationId = organization?.id;
  const agentId = teamMemberId || undefined; // TeamMember ID for filtering

  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      'handoffs-history',
      organizationId,
      agentId,
      status,
    ],
    queryFn: async ({ pageParam = 0 }) => {
      if (!organizationId) {
        return {
          handoffs: [],
          count: 0,
          totalCount: 0,
          statusCounts: EMPTY_STATUS_COUNTS,
          filters: {
            startDate: null,
            endDate: null,
            status: 'all',
            limit: PAGE_SIZE,
            offset: 0,
          },
        } as HandoffHistoryResponse;
      }

      // Handoffs surface as an all-time live queue, not a date-windowed
      // report, so the dashboard date range and per-page limit are
      // intentionally omitted — the backend pages the full queue by offset.
      const response = await getHandoffHistory({
        organizationId,
        agentId,
        status: status || 'all',
        offset: pageParam,
      });

      return response;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((acc, page) => acc + page.handoffs.length, 0);
      // If we got fewer items than requested, we've reached the end
      if (lastPage.handoffs.length < PAGE_SIZE) {
        return undefined;
      }
      // If total fetched equals or exceeds totalCount, we've reached the end
      if (totalFetched >= lastPage.totalCount) {
        return undefined;
      }
      return totalFetched;
    },
    enabled: enabled && !!organizationId,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
  });

  // Flatten all pages into a single array
  const allHandoffs = data?.pages.flatMap((page) => page.handoffs) || [];
  const totalCount = data?.pages[0]?.totalCount || 0;
  const statusCounts = data?.pages[0]?.statusCounts || EMPTY_STATUS_COUNTS;

  return {
    handoffs: allHandoffs,
    count: totalCount,
    stats: {
      total: totalCount,
      ...statusCounts,
    },
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    error: error as Error | null,
    refetch,
  };
};

/**
 * Hook to fetch handoffs stats summary for dashboard
 * Uses the /handoff-history endpoint and gets stats from statusCounts
 */
export const useHandoffStats = ({
  dateRange,
  allTime,
  enabled = true,
}: {
  dateRange?: {
    from: Date | null;
    to: Date | null;
  };
  allTime?: boolean;
  enabled?: boolean;
}) => {
  const { organization } = useOrganizationStore();
  const { teamMemberId } = useTeamMember();
  const organizationId = organization?.id;
  const agentId = teamMemberId || undefined; // TeamMember ID for filtering

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'handoffs-stats',
      organizationId,
      agentId,
      allTime ? 'all-time' : dateRange?.from?.toISOString(),
      allTime ? 'all-time' : dateRange?.to?.toISOString(),
    ],
    queryFn: async () => {
      if (!organizationId) {
        return { total: 0, ...EMPTY_STATUS_COUNTS };
      }

      // Single request — statusCounts in the response already has all per-status totals
      const result = await getHandoffHistory({
        organizationId,
        agentId,
        status: 'all',
        ...(allTime ? {} : {
          startDate: dateRange?.from?.toISOString().split('T')[0],
          endDate: dateRange?.to?.toISOString().split('T')[0],
        }),
        limit: 1,
      });

      return { total: result.totalCount, ...result.statusCounts };
    },
    enabled: enabled && !!organizationId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: false,
  });

  return {
    stats: data || { total: 0, ...EMPTY_STATUS_COUNTS },
    isLoading,
    error: error as Error | null,
    refetch,
  };
};
